//-- Copyright (c) 2021 TMT Insights Group, LLC
import { FilledInput, FormControl, Grow, IconButton, Tooltip } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import FilterChip from './Filters/FilterChip';
import styled from '@emotion/styled';
import PolarisIcon from './PolarisIcon';

type SearchBarProps = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

const TextSearch = ({ value, onChange, className }: SearchBarProps) => {
  const [textFilter, setTextFilter] = useState(value);
  const [view, setView] = useState<'no-filter' | 'edit' | 'filter'>('no-filter');
  const inputRef = useRef<HTMLInputElement>(null);
  const stashedTextFilter = useRef('');

  const onKeyEvent = useCallback((event: KeyboardEvent): void => {
    if (document.activeElement?.tagName?.toLocaleLowerCase() === 'body' && event.key === '/' && view !== 'edit') {
      stashedTextFilter.current = value;
      setTextFilter(value);
      setView('edit');
      event.stopPropagation();
      event.preventDefault();
    }
  }, [view, value]);

  useEffect(() => {
    document.addEventListener('keydown', onKeyEvent);

    return () => {
      document.removeEventListener('keydown', onKeyEvent);
    }
  }, [view, value]);

  useEffect(() => {
    if (value && value.length > 0) {
      setView('filter');
      setTextFilter(value);
    } else {
      setView('no-filter');
    }
  }, [value]);

  const onApply = (newFilter: string) => {
    onChange(newFilter);
    setTextFilter('');
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (!!textFilter && textFilter.trim().length > 0) {
        onApply(textFilter);
        setView('filter');
      } else {
        onApply('');
        setView('no-filter');
      }
    } else if (event.key === 'Escape') {
      setTextFilter(stashedTextFilter.current);
      if (!stashedTextFilter.current || stashedTextFilter.current.trim() === '') {
        setView('no-filter');
      } else {
        setView('filter');
      }
    }
  };

  const onClear = () => {
    setTextFilter('');
    setView('no-filter');
    onApply('');
  };

  const handleEndAdornmentClick = (e: any) => {
    e.preventDefault();
    if (textFilter.length > 0) {
      setTextFilter('');
      setTimeout(() => {
        if (inputRef) {
          inputRef.current?.focus();
        }
      }, 100);
    } else {
      setTextFilter('');
      setView('no-filter');
      onApply('');
    }
  };

  const getView = () => {
    switch (view) {
      case 'filter':
        return (
          <FilterChip
            data-testid="search-filter-value"
            label={
              <>
                Search: <strong style={{ marginLeft: 5 }}>&quot;{value}&quot;</strong>
              </>
            }
            isSelected
            onClear={onClear}
            onClick={() => {
              stashedTextFilter.current = value;
              setTextFilter(value);
              setView('edit');
            }}
          />
        );
      case 'edit':
        return (
          <Grow in timeout={400} style={{ transformOrigin: 'center left' }}>
            <FormControl variant="standard">
              <StyledFilledInput
                autoFocus
                inputRef={inputRef}
                sx={{ height: 37, minWidth: 150 }}
                id="search-bar"
                value={textFilter}
                onKeyUp={handleKeyUp}
                inputProps={{
                  'data-testid': 'text-filter-input',
                  'placeholder': 'Type \'/\' to search',
                }}
                onBlur={() => {
                  if (value && value.length === 0 && stashedTextFilter.current && stashedTextFilter.current.length === 0) {
                    setTextFilter('');
                    setView('no-filter');
                  } else if (value && value.length > 0) {
                    setTextFilter(value);
                    setView('filter');
                  } else if (stashedTextFilter.current && stashedTextFilter.current.length > 0) {
                    setTextFilter(stashedTextFilter.current);
                    setView('filter');
                  } else {
                    setTextFilter('');
                    setView('no-filter');
                  }
                }}
                onChange={(event) => setTextFilter(event.target.value)}
                endAdornment={
                  <EndAdornmentIcon
                    type={textFilter && textFilter.length ? 'close' : 'search'}
                    icon={textFilter && textFilter.length ? 'close_filled' : 'search'}
                    onMouseDown={handleEndAdornmentClick}
                  />
                }
              />
            </FormControl>
          </Grow>
        );
      case 'no-filter':
      default:
        return (
          <Tooltip title="Search">
            <IconButton
              data-testid="toggle-search"
              onClick={() => {
                setView('edit');
              }}
              aria-label="toggle search field"
            >
              <PolarisIcon icon="search" />
            </IconButton>
          </Tooltip>
        );
    }
  };

  return <div className={className}>{getView()}</div>;
}

export default TextSearch;

const EndAdornmentIcon = styled(PolarisIcon)<{ type: 'search' | 'close' }>`
  cursor: pointer;
  color: ${({ theme, type }) => (type === 'search' ? theme.palette.primary.main : 'inherit')};
  transition: ease opacity 200ms;
  width: 25px;
  height: 25px;
  opacity: ${({ type }) => (type === 'close' ? 0.3 : 0.7)};

  &:hover {
    opacity: ${({ type }) => (type === 'close' ? 0.5 : 1)};
    transition: ease opacity 200ms;
  }
`;

const StyledFilledInput = styled(FilledInput)`
  border: 0;
  border-radius: 50px;
  transition: ease opacity 2000ms;

  input {
    padding: ${({ theme }) => theme.spacing(1.25, 2)};

    &:-webkit-autofill {
      transition: background-color 50000s ease-in-out 0s !important;
      -webkit-box-shadow: 0 0 0 0px transparent inset !important;
    }
  }

  &::placeholder {
    opacity: 0.7;
  }
  &:after,
  &:before {
    display: none;
    border: 0;
  }
`;